import { useTranslation } from "react-i18next";
import type { TenantApartment } from "@/api/tenant/types";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import i18n from "@/i18n";

type TenantMetersTableProps = {
	meters: TenantApartment["meters"];
};

export function TenantMetersTable({ meters }: TenantMetersTableProps) {
	const { t } = useTranslation();

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t("tenant.meters.title")}</CardTitle>
			</CardHeader>
			<CardContent>
				{meters.length === 0 ? (
					<p className="text-muted-foreground py-4 text-center text-sm">
						{t("tenant.meters.noMeters")}
					</p>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("tenant.meters.serialNumber")}</TableHead>
								<TableHead>{t("tenant.meters.type")}</TableHead>
								<TableHead>{t("tenant.meters.lastCommunication")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{meters.map((meter) => (
								<TableRow key={meter.id}>
									<TableCell>
										<CopyableCell
											value={meter.serial_number}
											label={t("tenant.meters.serialNumber")}
										/>
									</TableCell>
									<TableCell>
										{t(`tenant.meterType.${meter.type}`, {
											defaultValue: meter.type,
										})}
									</TableCell>
									<TableCell>
										{meter.last_communication
											? new Intl.DateTimeFormat(i18n.language, {
													dateStyle: "medium",
													timeStyle: "short",
												}).format(new Date(meter.last_communication))
											: t("tenant.meters.noCommunication")}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
}
